import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Clock, Star, Eye, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';

export const ServiceCard = ({ service }) => {
  const {
    id,
    title,
    description,
    location,
    credits_per_hour,
    rating_average,
    rating_count,
    views_count,
    image_url,
    provider,
    category,
  } = service;

  const getCategoryImage = () => {
    if (image_url) return image_url;
    
    const categoryImages = {
      'jardinage': 'https://images.unsplash.com/photo-1621958206813-2e9c0441c5b0?w=400&h=300&fit=crop',
      'education': 'https://images.unsplash.com/photo-1758612897785-f6354d462789?w=400&h=300&fit=crop',
      'bricolage': 'https://images.unsplash.com/photo-1741388222137-c0d3007ec173?w=400&h=300&fit=crop',
      'cuisine': 'https://images.unsplash.com/photo-1665005499765-45bab6b1dddb?w=400&h=300&fit=crop',
      'digital': 'https://images.unsplash.com/photo-1498050108023-c5249f4df085?w=400&h=300&fit=crop',
      'bien-etre': 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=400&h=300&fit=crop',
    };
    
    return categoryImages[category?.slug] || null;
  };

  const cardImage = getCategoryImage();

  return (
    <Link to={`/services/${id}`} data-testid={`service-card-${id}`} className="block h-full">
      <Card className="group relative overflow-hidden rounded-2xl border border-border bg-card h-full transition-all duration-500 hover:shadow-xl hover:shadow-primary/5 hover:-translate-y-2 hover:border-primary/20">
        {/* Image or Category Icon */}
        <div className="relative h-44 bg-muted overflow-hidden">
          {cardImage ? (
            <>
              <img
                src={cardImage}
                alt={title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
            </>
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5">
              <span className="text-6xl transition-transform duration-500 group-hover:scale-125 group-hover:rotate-12">
                {category?.icon || '✨'}
              </span>
            </div>
          )}
          
          {/* Category Badge */}
          <Badge className="absolute top-3 left-3 bg-background/90 backdrop-blur-sm text-foreground border-0 shadow-lg">
            {category?.icon} {category?.name}
          </Badge>

          {/* Views - Appears on hover */}
          <div className="absolute top-3 right-3 flex items-center gap-1 text-xs bg-background/90 backdrop-blur-sm px-2 py-1 rounded-full opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
            <Eye className="w-3 h-3" />
            {views_count || 0}
          </div>

          {/* Quick action button - Appears on hover */}
          <div className="absolute bottom-3 right-3 opacity-0 translate-y-4 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300 delay-75">
            <Badge className="bg-primary text-primary-foreground shadow-lg cursor-pointer hover:bg-primary/90">
              Voir détails →
            </Badge>
          </div>
        </div>

        <CardContent className="p-5">
          {/* Title */}
          <h3 className="font-semibold text-lg mb-2 line-clamp-1 group-hover:text-primary transition-colors duration-300">
            {title}
          </h3>

          {/* Description */}
          <p className="text-sm text-muted-foreground line-clamp-2 mb-4 min-h-[40px]">
            {description}
          </p>

          {/* Provider */}
          <div className="flex items-center gap-2 mb-4">
            <div className={`relative ${provider?.verified ? '' : ''}`}>
              <Avatar className="h-9 w-9 transition-transform duration-300 group-hover:scale-110">
                <AvatarImage src={provider?.avatar_url} />
                <AvatarFallback className="bg-primary/10 text-primary text-sm font-medium">
                  {provider?.full_name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              {provider?.verified && (
                <CheckCircle className="absolute -bottom-0.5 -right-0.5 w-4 h-4 text-primary bg-background rounded-full" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{provider?.full_name}</p>
              {provider?.total_exchanges > 0 && (
                <p className="text-xs text-muted-foreground">
                  {provider.total_exchanges} échanges
                </p>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between pt-4 border-t border-border">
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5">
                <MapPin className="w-3.5 h-3.5" />
                <span className="truncate max-w-[80px]">{location}</span>
              </span>
              {rating_count > 0 && (
                <span className="flex items-center gap-1">
                  <Star className="w-3.5 h-3.5 text-secondary fill-secondary" />
                  <span className="font-medium text-foreground">{rating_average.toFixed(1)}</span>
                </span>
              )}
            </div>
            
            <div className="credit-badge transition-all duration-300 group-hover:shadow-glow-gold group-hover:scale-105">
              <Clock className="w-3.5 h-3.5 text-secondary" />
              <span className="font-bold">{credits_per_hour}</span>
            </div>
          </div>
        </CardContent>

        {/* Decorative corner accent */}
        <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-bl from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </Card>
    </Link>
  );
};

export default ServiceCard;
